import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import moment from 'moment';
import DateField from '../../DateField/DateField';
import { commonDateFormat, getDaysBetween, IFilterOptions, IShowNoDate } from './createDatesUtils';

interface IShippingDatesRow {
  filterOptions: IFilterOptions;
  showNoDate: IShowNoDate;
  daysToFinalize: number;
  setShippingStartDate: (val: any) => void;
  setShippingEndDate: (val: any) => void;
}

const startShippingLabel = 'Start Shipping (12:00 am)';
const endShippingLabel = 'End Shipping (11:59 pm)';

const ShippingDatesRow = forwardRef(
  (
    {
      filterOptions,
      showNoDate,
      daysToFinalize,
      setShippingStartDate,
      setShippingEndDate,
    }: IShippingDatesRow,
    ref: any
  ) => {
    const shippingStartDateRef = useRef<any>();
    const shippingEndDateRef = useRef<any>();

    useImperativeHandle(ref, () => ({
      showError: (error: string) => {
        if (shippingStartDateRef?.current?.showError) shippingStartDateRef.current.showError(error);
        if (shippingEndDateRef?.current?.showError) shippingEndDateRef.current.showError(error);
      },
    }));

    const shippingDays = !showNoDate.shippingEnd
      ? getDaysBetween(filterOptions.shippingDates?.startDate, filterOptions.shippingDates?.endDate)
      : '--';

    const earliestShippingStartDate = moment(
      filterOptions?.offerDates?.endDate,
      commonDateFormat
    ).add(daysToFinalize + 2, 'day');
    const nextEligibleShippingStartDate =
      !showNoDate.offerEnd && earliestShippingStartDate.isValid()
        ? earliestShippingStartDate.format(commonDateFormat)
        : '';

    const earliestShippingEndDate = moment(
      filterOptions?.shippingDates?.startDate,
      commonDateFormat
    ).add(1, 'day');
    const nextEligibleShippingEndDate =
      !showNoDate.shippingStart && earliestShippingEndDate.isValid()
        ? earliestShippingEndDate.format(commonDateFormat)
        : '';

    return (
      <div className="grid-x small-12 margin-top-2 px-create-date-filter-selector-container">
        <div className="cell small-2 create-dates-labels">Shipping ({shippingDays}D):</div>
        <DateField
          textfieldProps={{
            variant: 'outlined',
            label: !showNoDate.shippingStart && startShippingLabel,
            placeholder: showNoDate.shippingStart ? startShippingLabel : '',
            disabled: showNoDate.offerEnd,
            InputLabelProps: { shrink: true },
          }}
          disabled={{
            before: earliestShippingStartDate,
            after: !showNoDate.shippingEnd
              ? moment(filterOptions.shippingDates?.endDate, commonDateFormat).subtract(1, 'day')
              : undefined,
          }}
          date={
            showNoDate.shippingStart && nextEligibleShippingStartDate
              ? nextEligibleShippingStartDate
              : filterOptions?.shippingDates?.startDate ?? ''
          }
          setDate={setShippingStartDate}
          ref={shippingStartDateRef}
          showNoDate={showNoDate.shippingStart}
        />
        to
        <DateField
          textfieldProps={{
            variant: 'outlined',
            label: !showNoDate.shippingEnd && endShippingLabel,
            placeholder: showNoDate.shippingEnd ? endShippingLabel : '',
            disabled: showNoDate.shippingStart,
            InputLabelProps: { shrink: true },
          }}
          disabled={{
            before: earliestShippingEndDate,
          }}
          date={
            showNoDate.shippingEnd && nextEligibleShippingEndDate
              ? nextEligibleShippingEndDate
              : filterOptions?.shippingDates?.endDate
          }
          setDate={setShippingEndDate}
          ref={shippingEndDateRef}
          showNoDate={showNoDate.shippingEnd}
        />
      </div>
    );
  }
);

export default ShippingDatesRow;
